import type { ActionBarCrossSellCardModel } from '~/utils/shop/buildActionBarCrossSellCards';
import {
  buildCandidatesForRecommendedSku,
  type ProductsWithParentVariantsItem
} from '~/utils/shop/buildActionBarCrossSellCandidates';
import { accessoryPriceText } from '~/utils/lightingAdvisor/accessoryPriceText';
import { resolveProductImageUrlFromJson } from '~/utils/shop/resolveProductImageUrlFromJson';
import type { ActionBarCrossSellVariant } from '~/utils/shop/selectActionBarCrossSellProduct';
import {
  parseCableLengthMeters,
  sortActionBarCrossSellVariantsByLength
} from '~/utils/shop/sortActionBarCrossSellVariantsByLength';

const LENGTH_TOKEN = /,?\s*\b\d+(?:[.,]\d+)?\s*m\b/i;

function lengthTokenOf(value: string | null | undefined): string {
  const match = /\b(\d+(?:[.,]\d+)?\s*m)\b/i.exec(value ?? '');
  return match?.[1] ?? '';
}

function stripLengthToken(value: string): string {
  return value
    .replace(LENGTH_TOKEN, '')
    .replace(/[\s,;/-]+$/, '')
    .trim();
}

/**
 * Lighting Advisor accessories arrive as `related_products` of the light, so
 * cable lengths are sometimes siblings of one configurable parent and sometimes
 * standalone simple products. Group by parent when available, otherwise by the
 * product name without its length token.
 */
function groupKeyFor(
  variants: ReadonlyArray<ActionBarCrossSellVariant>
): string {
  if (variants.length > 1) {
    return `parent:${variants
      .map((variant) => variant.sku)
      .sort()
      .join('|')}`;
  }

  const variant = variants[0];
  const baseName = stripLengthToken(variant.name ?? '');
  if (baseName && lengthTokenOf(variant.lengthLabel || variant.name)) {
    return `family:${baseName.toLowerCase()}`;
  }
  return `sku:${variant.sku}`;
}

function cardIdFor(key: string): string {
  const [kind, value] = key.split(/:(.*)/s);
  return kind === 'sku' ? value : `${kind}-${slugify(value)}`;
}

function withLengthLabel(
  variant: ActionBarCrossSellVariant
): ActionBarCrossSellVariant {
  if (variant.lengthLabel) {
    return variant;
  }
  return { ...variant, lengthLabel: lengthTokenOf(variant.name) };
}

/** Shortest known length, mirroring the legacy shortest-cable recommendation. */
function defaultVariantSku(
  variants: ReadonlyArray<ActionBarCrossSellVariant>,
  fallbackSku: string
): string {
  const sorted = sortActionBarCrossSellVariantsByLength(variants);
  const shortest = sorted.find((variant) =>
    Number.isFinite(parseCableLengthMeters(variant.lengthLabel))
  );
  return shortest?.sku ?? fallbackSku;
}

/** Group ids end up in `data-testid` values, so keep them slug-safe. */
function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'group'
  );
}

export function buildLightingAdvisorAccessoryCards(
  skus: readonly string[],
  productsBySku: ReadonlyMap<string, ProductsWithParentVariantsItem>,
  locale: string,
  onRequestLabel?: string
): ActionBarCrossSellCardModel[] {
  const groups = new Map<
    string,
    {
      productSku: string;
      variantsBySku: Map<string, ActionBarCrossSellVariant>;
      isFamily: boolean;
    }
  >();

  for (const sku of skus) {
    const candidate = buildCandidatesForRecommendedSku(sku, productsBySku);
    if (!candidate?.variants.length) {
      continue;
    }

    const variants = candidate.variants.map(withLengthLabel);
    const key = groupKeyFor(variants);
    const group = groups.get(key) ?? {
      productSku: candidate.sku,
      variantsBySku: new Map<string, ActionBarCrossSellVariant>(),
      isFamily: key.startsWith('family:')
    };
    for (const variant of variants) {
      if (!group.variantsBySku.has(variant.sku)) {
        group.variantsBySku.set(variant.sku, variant);
      }
    }
    groups.set(key, group);
  }

  const cards: ActionBarCrossSellCardModel[] = [];

  for (const [key, group] of groups) {
    const variants = [...group.variantsBySku.values()];
    const selectedVariantSku = defaultVariantSku(variants, variants[0].sku);
    const selected =
      variants.find((variant) => variant.sku === selectedVariantSku) ??
      variants[0];
    const imageJson = selected.imageJson ?? null;
    const rawName = selected.name ?? group.productSku;
    const displayName =
      group.isFamily && variants.length > 1
        ? stripLengthToken(rawName) || rawName
        : rawName;

    cards.push({
      groupId: cardIdFor(key),
      groupLabel: displayName,
      productSku: group.productSku,
      displayName,
      selectedVariantSku,
      variants: sortActionBarCrossSellVariantsByLength(variants),
      priceDeltaText: accessoryPriceText({
        priceValue: selected.priceValue,
        currency: selected.currency,
        locale,
        onRequestLabel
      }),
      imageJson,
      imageUrl: resolveProductImageUrlFromJson(imageJson)
    });
  }

  return cards;
}
